import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  #isClosing = false;

  connectedCallback() {
    super.connectedCallback();

    this.refs.dialog.addEventListener('cancel', this.#handleCancel);
    this.refs.dialog.addEventListener('close', this.#handleNativeClose);

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('cancel', this.#handleCancel);
    this.refs.dialog.removeEventListener('close', this.#handleNativeClose);
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    dialog.showModal();
    this.dispatchEvent(new DialogOpenEvent());

    // Wait until the next tick to add the event listeners to avoid race condition
    // when `showDialog` is called within a click event listener.
    setTimeout(() => {
      if (!dialog.open) return;

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;
    if (this.#isClosing) return;

    this.#isClosing = true;
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    dialog.close();
    dialog.classList.remove('dialog-closing');
    this.#isClosing = false;

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Restores the native modal state if a DOM morph left an open dialog outside the top layer.
   */
  ensureModalDialog = () => {
    const { dialog } = this.refs;

    if (!dialog.open || this.#isModalDialog(dialog)) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    const previousAnimation = dialog.style.animation;
    const previousTransition = dialog.style.transition;

    dialog.classList.add('dialog-remodalizing');
    dialog.style.animation = 'none';
    dialog.style.transition = 'none';

    this.#isClosing = true;
    dialog.close();
    this.#isClosing = false;

    dialog.showModal();

    if (dialog.hasAttribute('scroll-lock')) {
      document.documentElement.setAttribute('scroll-lock', '');
    }

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeyDown);

    requestAnimationFrame(() => {
      dialog.classList.remove('dialog-remodalizing');
      dialog.style.animation = previousAnimation;
      dialog.style.transition = previousTransition;
    });
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    // Removed cart rows may detach before this bubbling click reaches the drawer.
    // Its original event path still proves the click started inside the dialog.
    if (this.matches('cart-drawer-component') && event.composedPath().includes(dialog) && event.target !== dialog) return;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Routes native dialog cancellation through the regular close flow.
   *
   * @param {Event} event - The cancel event.
   */
  #handleCancel = (event) => {
    event.preventDefault();
    this.closeDialog();
  };

  /**
   * Cleans up if the browser closes the native dialog outside of closeDialog().
   */
  #handleNativeClose = () => {
    const wasClosing = this.#isClosing;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.refs.dialog.classList.remove('dialog-closing');
    this.#isClosing = false;

    if (this.refs.dialog.hasAttribute('scroll-lock')) {
      document.documentElement.removeAttribute('scroll-lock');
    }

    if (!wasClosing) {
      this.dispatchEvent(new DialogCloseEvent());
    }
  };

  /**
   * Checks whether the native dialog is currently in the modal top layer.
   *
   * @param {HTMLDialogElement} dialog - The dialog element.
   * @returns {boolean}
   */
  #isModalDialog(dialog) {
    try {
      return dialog.matches(':modal');
    } catch {
      return true;
    }
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDialogElement || event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
